import { useEffect, Suspense, lazy } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n/config';
import { isReservedSlug } from '@/lib/courierStorefront';

// Storefront + 404 are lazy so the entry chunk stays lean. The fallback
// is deliberately neutral (no EuroCollis navy chrome) so a storefront
// never flashes platform branding before its own theme paints.
const CourierStorefront = lazy(() => import('@/pages/CourierStorefront'));
const NotFound = lazy(() => import('@/pages/NotFound'));
import StorefrontSkeleton from '@/components/storefront/StorefrontSkeleton';

const LanguageRoute = () => {
  const { lang } = useParams<{ lang: string }>();
  const { i18n } = useTranslation();
  const isValid = !!lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);

  useEffect(() => {
    if (isValid && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, isValid, i18n]);

  if (isValid) return <Outlet />;

  // Not a supported language — dispatch single-segment paths to the
  // courier storefront (e.g. /atlastransport). Reserved words → 404.
  if (!lang || isReservedSlug(lang)) {
    return (
      <Suspense fallback={<StorefrontSkeleton />}>
        <NotFound />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<StorefrontSkeleton />}>
      <CourierStorefront />
    </Suspense>
  );
};

export default LanguageRoute;
